import { Component } from "@theme/component";
import { DialogComponent, DialogCloseEvent } from "@theme/dialog";
import { CartAddEvent, CartGroupedSections } from "@theme/events";
import { fetchConfig, formatShopifyMoney } from "@theme/utilities";

/**
 * Extended warranties, ported from the Mobile Pixels theme.
 *
 * Warranties are configured by the Shuffle Life Services app, which stores a
 * map in the shop-level `warranties/config` JSON metafield keyed by product
 * ID: `{ "<productId>": { id, title, variants: [{ id, title, price }] } }`.
 * Prices are in major currency units. The chosen warranty variant is added to
 * the cart as its own line item, linked to the covered product through the
 * `_linked_id` line item property (the covered product's variant ID).
 *
 * The metafield value is exposed to JS as `window.FoxTheme.settings.warranties`
 * in scripts.liquid, which also loads this module only when the metafield is set.
 */

export const WarrantyEvents = {
  open: "warranties-modal:open",
  modalChanged: "warranties-modal:changed",
  selectionChanged: "product-warranties:changed",
};

/**
 * Returns the warranty config for a product, or null when none is set up.
 *
 * @param {string|number|undefined} productId
 * @returns {{ variants: Array<{id: string|number, title: string, price: string|number}> } | null}
 */
export function getWarrantyConfig(productId) {
  if (!productId) return null;
  const config = window.FoxTheme?.settings?.warranties?.[String(productId)];
  return config?.variants?.length ? config : null;
}

/**
 * Collects the section ids that cart updates should re-render.
 *
 * @returns {string[]}
 */
function gatherGroupedSectionIds() {
  const sections = [];
  document.dispatchEvent(new CartGroupedSections(sections));
  return [...new Set(sections)];
}

/**
 * Adds a warranty variant to the cart, linked to the covered product's variant.
 *
 * @param {string|number} warrantyVariantId
 * @param {string|number} linkedVariantId - Variant ID of the covered product.
 * @returns {Promise<Object>} The cart add response (including `sections`).
 */
export async function addWarrantyToCart(warrantyVariantId, linkedVariantId) {
  const body = JSON.stringify({
    items: [
      {
        id: Number(warrantyVariantId),
        quantity: 1,
        properties: {
          _linked_id: String(linkedVariantId),
          _unique_line_id: Date.now().toString(),
        },
      },
    ],
    sections: gatherGroupedSectionIds().join(","),
    sections_url: window.location.pathname,
  });

  const response = await fetch(FoxTheme.routes.cart_add_url, fetchConfig("json", { body }));
  const responseJson = await response.json();

  if (responseJson.status) {
    throw new Error(responseJson.description || responseJson.message || "Failed to add warranty");
  }

  return responseJson;
}

/**
 * Continues a product form submission for products that have warranties.
 * Called by product-form.js after the main product was added to the cart but
 * before the CartAddEvent is dispatched (the event opens the cart drawer).
 *
 * - Warranty pre-selected on the page: adds it, then dispatches the held event
 *   with the fresher sections from the warranty response.
 * - No selection but the warranties modal exists: opens the modal linked to
 *   the just-added variant; the modal dispatches its own CartAddEvent on
 *   purchase, or the held event is dispatched when the modal is dismissed.
 *
 * @param {{ productForm: HTMLElement & { dispatchEvent: Function }, formData: FormData, cartAddEvent: CartAddEvent }} options
 * @returns {boolean} True when this module took over dispatching the event.
 */
export function handleProductFormCartAdd({ productForm, formData, cartAddEvent }) {
  const productId = productForm.dataset.productId;
  if (!getWarrantyConfig(productId)) return false;

  const linkedVariantId = formData.get("id");
  if (!linkedVariantId) return false;

  const warrantyComponent = productForm
    .closest(".shopify-section, dialog")
    ?.querySelector("product-warranties-component");
  const selectedWarrantyId = warrantyComponent?.selectedVariantId;

  if (selectedWarrantyId) {
    addWarrantyToCart(selectedWarrantyId, linkedVariantId)
      .then((warrantyResponse) => {
        if (warrantyResponse.sections) {
          cartAddEvent.detail.data.sections = warrantyResponse.sections;
        }
      })
      .catch((error) => {
        console.error("Failed to add warranty to cart:", error);
      })
      .finally(() => {
        productForm.dispatchEvent(cartAddEvent);
      });
    return true;
  }

  const modal = document.querySelector("warranties-modal-component");
  if (!modal) return false;

  document.dispatchEvent(
    new CustomEvent(WarrantyEvents.open, {
      detail: {
        productId: String(productId),
        linkedId: String(linkedVariantId),
        itemCount: cartAddEvent.detail.data.itemCount,
        onComplete: (warrantyAdded) => {
          if (!warrantyAdded) productForm.dispatchEvent(cartAddEvent);
        },
      },
    })
  );
  return true;
}

/**
 * The on-page warranty option group rendered by snippets/product-warranties.liquid.
 *
 * @typedef {object} ProductWarrantiesRefs
 * @property {HTMLInputElement[]} [warrantyInputs] - The warranty option checkboxes.
 * @extends Component<ProductWarrantiesRefs>
 */
export class ProductWarrantiesComponent extends Component {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    if (!getWarrantyConfig(this.dataset.productId)) {
      this.hidden = true;
      return;
    }

    document.addEventListener(WarrantyEvents.modalChanged, this.#onModalChanged, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /** @returns {string | null} The selected warranty variant ID. */
  get selectedVariantId() {
    return (this.refs.warrantyInputs ?? []).find((input) => input.checked)?.value ?? null;
  }

  /**
   * Enforces single-select and broadcasts the selection.
   *
   * @param {Event} event
   */
  onChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    if (target.checked) {
      for (const input of this.refs.warrantyInputs ?? []) {
        if (input !== target) input.checked = false;
      }
    }

    document.dispatchEvent(
      new CustomEvent(WarrantyEvents.selectionChanged, {
        detail: {
          productId: String(this.dataset.productId),
          variantId: target.checked ? target.value : null,
        },
      })
    );
  }

  /**
   * Opens the warranties modal in "see details" mode.
   */
  openModal() {
    document.dispatchEvent(
      new CustomEvent(WarrantyEvents.open, {
        detail: { productId: String(this.dataset.productId), linkedId: null },
      })
    );
  }

  /**
   * Mirrors a selection made inside the modal back onto the page options.
   *
   * @param {CustomEvent} event
   */
  #onModalChanged = (event) => {
    const { productId, variantId } = event.detail ?? {};
    if (String(productId) !== String(this.dataset.productId)) return;

    for (const input of this.refs.warrantyInputs ?? []) {
      input.checked = variantId != null && input.value === String(variantId);
    }
  };
}

if (!customElements.get("product-warranties-component")) {
  customElements.define("product-warranties-component", ProductWarrantiesComponent);
}

/**
 * The warranties modal rendered by sections/warranties-modal.liquid.
 * A single instance serves every product; option values and prices are
 * refreshed from the warranty config whenever it opens for a new product.
 *
 * @typedef {object} WarrantiesModalRefs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLInputElement[]} [modalInputs] - The warranty option checkboxes.
 * @property {HTMLElement[]} [modalTitles] - The option title elements.
 * @property {HTMLElement[]} [modalPrices] - The option price elements.
 * @property {HTMLButtonElement} [acceptButton] - The purchase button.
 * @extends DialogComponent
 */
export class WarrantiesModalComponent extends DialogComponent {
  #abortController = new AbortController();

  /** @type {string | null} */
  #productId = null;

  /** @type {string | null} */
  #linkedId = null;

  /** @type {number} */
  #itemCount = 0;

  /** @type {((warrantyAdded: boolean) => void) | null} */
  #onComplete = null;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    document.addEventListener(WarrantyEvents.open, this.#onOpenRequest, { signal });
    document.addEventListener(WarrantyEvents.selectionChanged, this.#onPageSelectionChanged, { signal });
    this.addEventListener(DialogCloseEvent.eventName, this.#onDialogClosed, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /** @returns {string | null} The selected warranty variant ID. */
  get selectedVariantId() {
    return (this.refs.modalInputs ?? []).find((input) => input.checked)?.value || null;
  }

  /**
   * Handles `warranties-modal:open` events.
   *
   * @param {CustomEvent} event
   */
  #onOpenRequest = (event) => {
    const { productId, linkedId = null, itemCount = 0, onComplete = null } = event.detail ?? {};

    if (!this.#setProduct(productId)) {
      onComplete?.(false);
      return;
    }

    this.#linkedId = linkedId ? String(linkedId) : null;
    this.#itemCount = itemCount;
    this.#onComplete = onComplete;

    // "See details" mode (opened from the info icon) has nothing to purchase against.
    this.refs.acceptButton?.classList.toggle("hidden", !this.#linkedId);
    this.#syncAcceptState();
    this.showDialog();
  };

  /**
   * Points the modal options at a product's warranty config.
   *
   * @param {string|number|undefined} productId
   * @returns {boolean} False when the product has no warranties configured.
   */
  #setProduct(productId) {
    const config = getWarrantyConfig(productId);
    if (!config) return false;

    const id = String(productId);
    if (this.#productId === id) return true;
    this.#productId = id;

    const { modalInputs = [], modalTitles = [], modalPrices = [] } = this.refs;
    modalInputs.forEach((input, index) => {
      const variant = config.variants[index];
      input.checked = false;
      input.value = variant?.id != null ? String(variant.id) : "";
      input.closest(".warranties-modal__option")?.classList.toggle("hidden", !variant);
      if (!variant) return;
      if (modalTitles[index]) modalTitles[index].textContent = variant.title;
      if (modalPrices[index]) {
        modalPrices[index].textContent = formatShopifyMoney(Math.round(parseFloat(variant.price) * 100));
      }
    });

    return true;
  }

  /**
   * Enforces single-select, updates the purchase button, and mirrors the
   * selection back to the on-page options.
   *
   * @param {Event} event
   */
  onOptionChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    if (target.checked) {
      for (const input of this.refs.modalInputs ?? []) {
        if (input !== target) input.checked = false;
      }
    }
    this.#syncAcceptState();

    document.dispatchEvent(
      new CustomEvent(WarrantyEvents.modalChanged, {
        detail: {
          productId: this.#productId,
          variantId: target.checked ? target.value : null,
        },
      })
    );
  }

  /**
   * Adds the selected warranty to the cart (linked to the covered variant)
   * and dispatches the cart update.
   */
  async accept() {
    const warrantyVariantId = this.selectedVariantId;
    if (!warrantyVariantId || !this.#linkedId) return;

    // Loading state is driven by aria-busy instead of .btn--loading: any
    // `btn--*` class matches theme.css's `[class*=btn--]::before` hover-fill
    // overlay, which would paint the button black while loading.
    const button = this.refs.acceptButton;
    button?.setAttribute("aria-busy", "true");

    try {
      const response = await addWarrantyToCart(warrantyVariantId, this.#linkedId);

      this.dispatchEvent(
        new CartAddEvent(response, warrantyVariantId, {
          source: "warranties-modal",
          itemCount: this.#itemCount,
          productId: this.#productId ?? undefined,
          sections: response.sections,
        })
      );

      const complete = this.#onComplete;
      this.#onComplete = null;
      complete?.(true);
    } catch (error) {
      console.error("Failed to add warranty to cart:", error);
    } finally {
      button?.removeAttribute("aria-busy");
      this.closeDialog();
    }
  }

  /**
   * Dismisses the modal without protection.
   */
  reject() {
    for (const input of this.refs.modalInputs ?? []) input.checked = false;
    this.#syncAcceptState();

    document.dispatchEvent(
      new CustomEvent(WarrantyEvents.modalChanged, {
        detail: { productId: this.#productId, variantId: null },
      })
    );

    this.closeDialog();
  }

  /**
   * Resolves a pending completion callback when the dialog closes without a
   * purchase (reject button, backdrop click, or Escape).
   */
  #onDialogClosed = () => {
    const complete = this.#onComplete;
    this.#onComplete = null;
    complete?.(false);
  };

  /**
   * Keeps the modal options in sync with the on-page selection.
   *
   * @param {CustomEvent} event
   */
  #onPageSelectionChanged = (event) => {
    const { productId, variantId } = event.detail ?? {};
    if (!this.#setProduct(productId)) return;

    for (const input of this.refs.modalInputs ?? []) {
      input.checked = variantId != null && input.value === String(variantId);
    }
    this.#syncAcceptState();
  };

  #syncAcceptState() {
    const button = this.refs.acceptButton;
    if (!button) return;
    button.disabled = !this.selectedVariantId;
  }
}

if (!customElements.get("warranties-modal-component")) {
  customElements.define("warranties-modal-component", WarrantiesModalComponent);
}

/**
 * The "Add Extended Warranty Protection" button rendered under eligible cart
 * lines by snippets/cart-products.liquid.
 */
export class LineItemWarrantiesComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (!getWarrantyConfig(this.dataset.productId) || !document.querySelector("warranties-modal-component")) {
      this.hidden = true;
    }
  }

  /**
   * Opens the warranties modal linked to this cart line's variant.
   */
  openModal() {
    document.dispatchEvent(
      new CustomEvent(WarrantyEvents.open, {
        detail: {
          productId: String(this.dataset.productId),
          linkedId: String(this.dataset.linkedId),
          itemCount: 0,
        },
      })
    );
  }
}

if (!customElements.get("line-item-warranties-component")) {
  customElements.define("line-item-warranties-component", LineItemWarrantiesComponent);
}
